import {ChatStorageError, setChatStorageItem} from '../utils/chatStorage';

export interface MatrixSessionLockScope {
    tenant: string;
    userId: string;
}

export type MatrixSessionLockState = 'active' | 'standby' | 'takeover_pending';

interface MatrixSessionLockRecord {
    ownerId: string;
    updatedAtMs: number;
}

interface MatrixSessionLockCallbacks {
    onLockLost?: () => void | Promise<void>;
}

interface OwnedLock {
    scope: MatrixSessionLockScope;
    key: string;
    callbacks?: MatrixSessionLockCallbacks;
    heartbeatId: ReturnType<typeof window.setInterval>;
    releaseInFlight: Promise<void> | null;
}

interface LockSnapshot {
    state: MatrixSessionLockState;
    ownerId: string | null;
    updatedAtMs: number | null;
    claimPending: boolean;
}

interface PendingClaim {
    claimantKey: string;
    startedAtMs: number;
    targetOwnerId: string | null;
}

type LockSubscriber = (snapshot: LockSnapshot) => void;
type LockReporter = (level: 'info' | 'warn', message: string, context: Record<string, unknown>) => void;

const LOCK_KEY_PREFIX = 'matrix-session-lock';
const CLAIMANT_KEY_PREFIX = 'matrix-session-lock-claimant';
export const MATRIX_SESSION_LOCK_HEARTBEAT_MS = 5_000;
// Browsers throttle timers in backgrounded tabs to one tick per minute, so a
// healthy owner may go up to ~60s between heartbeats. The staleness cutoff must
// comfortably exceed that or standby windows steal the lock from a live owner.
export const MATRIX_SESSION_LOCK_STALE_MS = 120_000;
export const MATRIX_SESSION_LOCK_CLAIM_STALE_MS = 15_000;
export const MATRIX_SESSION_LOCK_CLAIM_GRACE_MS = 10_000;
export const MATRIX_SESSION_LOCK_CLAIM_MAX_MS = 60_000;
const MATRIX_SESSION_LOCK_POLL_MS = 1_000;

const ownerId = (() => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
})();

const ownedLocks = new Map<string, OwnedLock>();
const subscribers = new Map<string, Set<LockSubscriber>>();
const pendingClaims = new Map<string, PendingClaim>();
let report: LockReporter = () => {};
let pollIntervalId: ReturnType<typeof window.setInterval> | null = null;
let isGlobalListenerAttached = false;

export const setMatrixSessionLockReporter = (reporter: LockReporter): void => {
    report = reporter;
};

function logTransition(event: string, message: string, extra: Record<string, unknown> = {}): void {
    report('info', `[WEB CHAT] Matrix session lock ${message}`, {
        source: 'chat',
        event,
        ...extra,
    });
}

function buildLockKey(scope: MatrixSessionLockScope): string {
    return `${LOCK_KEY_PREFIX}:${scope.tenant}:${scope.userId}`;
}

function buildClaimantKey(scope: MatrixSessionLockScope): string {
    return `${CLAIMANT_KEY_PREFIX}:${scope.tenant}:${scope.userId}`;
}

function buildLockKeyFromClaimantKey(key: string): string {
    return `${LOCK_KEY_PREFIX}:${key.slice(`${CLAIMANT_KEY_PREFIX}:`.length)}`;
}

function buildClaimantKeyFromLockKey(key: string): string {
    return `${CLAIMANT_KEY_PREFIX}:${key.slice(`${LOCK_KEY_PREFIX}:`.length)}`;
}

function readLockRecord(key: string): MatrixSessionLockRecord | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<MatrixSessionLockRecord>;
        if (typeof parsed.ownerId !== 'string' || typeof parsed.updatedAtMs !== 'number') {
            return null;
        }
        return {
            ownerId: parsed.ownerId,
            updatedAtMs: parsed.updatedAtMs,
        };
    } catch {
        return null;
    }
}

function writeLockRecord(key: string): void {
    setChatStorageItem(key, JSON.stringify({
        ownerId,
        updatedAtMs: Date.now(),
    } satisfies MatrixSessionLockRecord));
}

// Heartbeat and wake refreshes run outside any caller that could handle the
// error; the failure is already logged with its key, so keep the timer alive.
function refreshLockRecord(key: string): void {
    try {
        writeLockRecord(key);
    } catch (error) {
        if (!(error instanceof ChatStorageError)) throw error;
    }
}

function writeClaimantRecord(key: string): void {
    setChatStorageItem(key, JSON.stringify({
        ownerId,
        updatedAtMs: Date.now(),
    } satisfies MatrixSessionLockRecord));
}

function isRecordStale(record: MatrixSessionLockRecord | null): boolean {
    return !record || Date.now() - record.updatedAtMs > MATRIX_SESSION_LOCK_STALE_MS;
}

function isClaimantStale(record: MatrixSessionLockRecord): boolean {
    return Date.now() - record.updatedAtMs > MATRIX_SESSION_LOCK_CLAIM_STALE_MS;
}

function getLiveOtherClaimant(key: string): MatrixSessionLockRecord | null {
    const claimant = readLockRecord(buildClaimantKeyFromLockKey(key));
    if (!claimant || claimant.ownerId === ownerId || isClaimantStale(claimant)) {
        return null;
    }
    return claimant;
}

function isOwnerUnresponsiveToClaim(key: string, record: MatrixSessionLockRecord | null): boolean {
    const claim = pendingClaims.get(key);
    if (!claim || !record || record.ownerId === ownerId) return false;

    return Date.now() - claim.startedAtMs >= MATRIX_SESSION_LOCK_CLAIM_GRACE_MS
        && record.updatedAtMs < claim.startedAtMs;
}

function dropPendingClaim(key: string, event: string, message: string): void {
    logTransition(event, message);
    cancelPendingClaim(key);
    notifySubscribers(key);
}

function refreshPendingClaims(): void {
    Array.from(pendingClaims.entries()).forEach(([key, claim]) => {
        if (Date.now() - claim.startedAtMs >= MATRIX_SESSION_LOCK_CLAIM_MAX_MS) {
            dropPendingClaim(key, 'session_lock_claim_expired', 'takeover claim expired');
            return;
        }
        const record = readLockRecord(key);
        if (record && record.ownerId !== ownerId && record.ownerId !== claim.targetOwnerId) {
            dropPendingClaim(key, 'session_lock_claim_superseded', 'takeover claim superseded by another window');
            return;
        }
        if (getLiveOtherClaimant(key)) return;
        try {
            writeClaimantRecord(claim.claimantKey);
        } catch (error) {
            if (!(error instanceof ChatStorageError)) throw error;
        }
    });
}

function cancelPendingClaim(key: string): void {
    const claim = pendingClaims.get(key);
    if (!claim) return;

    pendingClaims.delete(key);
    const claimant = readLockRecord(claim.claimantKey);
    if (claimant?.ownerId === ownerId) {
        localStorage.removeItem(claim.claimantKey);
    }
    updatePolling();
}

function cancelAllPendingClaims(): void {
    Array.from(pendingClaims.keys()).forEach(cancelPendingClaim);
}

function clearClaimantIfCurrentOwner(key: string): void {
    const claimantKey = buildClaimantKeyFromLockKey(key);
    const claimant = readLockRecord(claimantKey);
    if (claimant?.ownerId === ownerId) {
        localStorage.removeItem(claimantKey);
    }
    pendingClaims.delete(key);
}

function getSnapshot(key: string): LockSnapshot {
    const record = readLockRecord(key);
    const ownedLock = ownedLocks.get(key);
    const isActiveOwner = record && record.ownerId === ownerId && !ownedLock?.releaseInFlight;
    const isAvailable = (isRecordStale(record) || isOwnerUnresponsiveToClaim(key, record))
        && !getLiveOtherClaimant(key);

    return {
        state: isActiveOwner || isAvailable ? 'active' : 'standby',
        ownerId: record?.ownerId ?? null,
        updatedAtMs: record?.updatedAtMs ?? null,
        claimPending: pendingClaims.has(key),
    };
}

function notifySubscribers(key: string): void {
    const callbacks = subscribers.get(key);
    if (!callbacks?.size) return;

    const snapshot = getSnapshot(key);
    callbacks.forEach(callback => callback(snapshot));
}

function stopOwnedLock(key: string, notifyLoss: boolean, clearRecord: boolean = false): void {
    const ownedLock = ownedLocks.get(key);
    if (!ownedLock) return;

    window.clearInterval(ownedLock.heartbeatId);
    ownedLocks.delete(key);
    if (clearRecord) {
        const record = readLockRecord(key);
        if (record?.ownerId === ownerId) {
            localStorage.removeItem(key);
        }
    }
    if (notifyLoss) {
        logTransition('session_lock_lost', 'lost to another window');
        try {
            void Promise.resolve(ownedLock.callbacks?.onLockLost?.()).catch(error => {
                console.error('Failed to stop Matrix session after losing its lock', error);
            });
        } catch (error) {
            console.error('Failed to stop Matrix session after losing its lock', error);
        }
    }
    updatePolling();
}

function releaseOwnedLockAfterShutdown(key: string): void {
    const ownedLock = ownedLocks.get(key);
    if (!ownedLock || ownedLock.releaseInFlight) return;

    // Match Element's handoff: keep servicing the current ownership record
    // while the application shuts its Matrix client down. The claimant cannot
    // open the same Rust Crypto IndexedDB until this promise has settled.
    logTransition('session_lock_owner_releasing', 'owner releasing for claimant');
    const release = (async () => {
        await ownedLock.callbacks?.onLockLost?.();

        if (ownedLocks.get(key) !== ownedLock) return;

        window.clearInterval(ownedLock.heartbeatId);
        ownedLocks.delete(key);
        const record = readLockRecord(key);
        if (record?.ownerId === ownerId) {
            localStorage.removeItem(key);
        }
        updatePolling();
        logTransition('session_lock_released', 'released after shutdown');
        notifySubscribers(key);
    })();

    ownedLock.releaseInFlight = release;
    void release.catch(error => {
        // Fail closed: retaining and servicing the lock is safer than allowing
        // another client to open a database which may still be in use.
        console.error('Failed to release Matrix session lock after shutdown', error);
    });
}

function acknowledgeClaimAndRelease(key: string): void {
    refreshLockRecord(key);
    releaseOwnedLockAfterShutdown(key);
}

function verifyOwnedLocks(): void {
    ownedLocks.forEach((ownedLock, key) => {
        const record = readLockRecord(key);
        if (!record || record.ownerId !== ownerId) {
            stopOwnedLock(key, !ownedLock.releaseInFlight);
            return;
        }

        if (ownedLock.releaseInFlight) {
            refreshLockRecord(key);
            return;
        }

        if (getLiveOtherClaimant(key)) {
            acknowledgeClaimAndRelease(key);
            notifySubscribers(key);
        }
    });
}

function pollLocks(): void {
    verifyOwnedLocks();
    refreshPendingClaims();
    subscribers.forEach((_callbacks, key) => notifySubscribers(key));
}

function updatePolling(): void {
    const shouldPoll = ownedLocks.size > 0 || subscribers.size > 0 || pendingClaims.size > 0;
    if (shouldPoll && pollIntervalId === null) {
        pollIntervalId = window.setInterval(pollLocks, MATRIX_SESSION_LOCK_POLL_MS);
    } else if (!shouldPoll && pollIntervalId !== null) {
        window.clearInterval(pollIntervalId);
        pollIntervalId = null;
    }
}

function releaseOwnedLocks(): void {
    cancelAllPendingClaims();
    Array.from(ownedLocks.values()).forEach(ownedLock => {
        releaseMatrixSessionLock(ownedLock.scope);
    });
}

// A frozen tab misses heartbeats and takeovers entirely; on wake, immediately
// re-verify ownership (stop if another window took over) and refresh surviving
// heartbeats instead of waiting for the next interval tick.
function handleWake(): void {
    verifyOwnedLocks();
    refreshPendingClaims();
    ownedLocks.forEach((_ownedLock, key) => refreshLockRecord(key));
    subscribers.forEach((_callbacks, key) => notifySubscribers(key));
}

function attachGlobalListeners(): void {
    if (isGlobalListenerAttached) return;

    window.addEventListener('storage', (event) => {
        if (event.key?.startsWith(`${LOCK_KEY_PREFIX}:`)) {
            verifyOwnedLocks();
            notifySubscribers(event.key);
        } else if (event.key?.startsWith(`${CLAIMANT_KEY_PREFIX}:`)) {
            const lockKey = buildLockKeyFromClaimantKey(event.key);
            verifyOwnedLocks();
            notifySubscribers(lockKey);
        }
    });
    window.addEventListener('pagehide', releaseOwnedLocks);
    window.addEventListener('beforeunload', releaseOwnedLocks);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') handleWake();
    });
    window.addEventListener('pageshow', handleWake);
    isGlobalListenerAttached = true;
}

function startHeartbeat(scope: MatrixSessionLockScope, key: string, callbacks?: MatrixSessionLockCallbacks): void {
    const existing = ownedLocks.get(key);
    if (existing) {
        existing.callbacks = callbacks;
        return;
    }

    const heartbeatId = window.setInterval(() => {
        const ownedLock = ownedLocks.get(key);
        if (!ownedLock) return;

        const record = readLockRecord(key);
        if (!record || record.ownerId !== ownerId) {
            stopOwnedLock(key, !ownedLock.releaseInFlight);
            notifySubscribers(key);
            return;
        }
        if (ownedLock.releaseInFlight) {
            refreshLockRecord(key);
            notifySubscribers(key);
            return;
        }
        if (getLiveOtherClaimant(key)) {
            acknowledgeClaimAndRelease(key);
            notifySubscribers(key);
            return;
        }
        refreshLockRecord(key);
        notifySubscribers(key);
    }, MATRIX_SESSION_LOCK_HEARTBEAT_MS);

    ownedLocks.set(key, {
        scope,
        key,
        callbacks,
        heartbeatId,
        releaseInFlight: null,
    });
    updatePolling();
}

export function acquireMatrixSessionLock(
    scope: MatrixSessionLockScope,
    callbacks?: MatrixSessionLockCallbacks,
): MatrixSessionLockState {
    attachGlobalListeners();

    const key = buildLockKey(scope);
    const currentRecord = readLockRecord(key);
    const forcedTakeover = !!currentRecord
        && currentRecord.ownerId !== ownerId
        && !isRecordStale(currentRecord)
        && isOwnerUnresponsiveToClaim(key, currentRecord);
    if (currentRecord && currentRecord.ownerId !== ownerId && !isRecordStale(currentRecord) && !forcedTakeover) {
        logTransition('session_lock_standby', 'standby', {
            blockedBy: 'owner',
            recordAgeMs: Date.now() - currentRecord.updatedAtMs,
        });
        updatePolling();
        return 'standby';
    }
    const otherClaimant = getLiveOtherClaimant(key);
    if (otherClaimant) {
        logTransition('session_lock_standby', 'standby', {
            blockedBy: 'claimant',
            recordAgeMs: Date.now() - otherClaimant.updatedAtMs,
        });
        updatePolling();
        return 'standby';
    }

    writeLockRecord(key);
    const nextRecord = readLockRecord(key);
    if (nextRecord?.ownerId !== ownerId) {
        logTransition('session_lock_standby', 'standby', {blockedBy: 'race'});
        updatePolling();
        return 'standby';
    }

    if (forcedTakeover && currentRecord) {
        logTransition('session_lock_forced_takeover', 'forced takeover of unresponsive owner', {
            recordAgeMs: Date.now() - currentRecord.updatedAtMs,
        });
    }
    clearClaimantIfCurrentOwner(key);
    logTransition('session_lock_acquired', 'acquired');
    startHeartbeat(scope, key, callbacks);
    notifySubscribers(key);
    return 'active';
}

export function requestMatrixSessionLockTakeover(scope: MatrixSessionLockScope): boolean {
    attachGlobalListeners();

    const key = buildLockKey(scope);
    const claimantKey = buildClaimantKey(scope);
    const startedAtMs = Date.now();
    const targetOwnerId = readLockRecord(key)?.ownerId;
    try {
        writeClaimantRecord(claimantKey);
        if (!pendingClaims.has(key)) {
            pendingClaims.set(key, {
                claimantKey,
                startedAtMs,
                targetOwnerId: targetOwnerId && targetOwnerId !== ownerId ? targetOwnerId : null,
            });
        }
    } catch (error) {
        if (!(error instanceof ChatStorageError)) throw error;
        return false;
    }
    logTransition('session_lock_takeover_requested', 'takeover requested');
    notifySubscribers(key);
    updatePolling();
    return true;
}

export function hasPendingMatrixSessionLockClaim(scope: MatrixSessionLockScope): boolean {
    return pendingClaims.has(buildLockKey(scope));
}

export function cancelMatrixSessionLockTakeover(scope: MatrixSessionLockScope): void {
    const key = buildLockKey(scope);
    if (!pendingClaims.has(key)) return;

    cancelPendingClaim(key);
    notifySubscribers(key);
}

export function releaseMatrixSessionLock(scope: MatrixSessionLockScope): void {
    const key = buildLockKey(scope);
    const record = readLockRecord(key);

    stopOwnedLock(key, false);
    cancelPendingClaim(key);
    if (record?.ownerId === ownerId) {
        localStorage.removeItem(key);
        logTransition('session_lock_released', 'released');
        notifySubscribers(key);
    }
}

export function subscribeToMatrixSessionLock(
    scope: MatrixSessionLockScope,
    callback: LockSubscriber,
): () => void {
    attachGlobalListeners();

    const key = buildLockKey(scope);
    const callbacks = subscribers.get(key) ?? new Set<LockSubscriber>();
    callbacks.add(callback);
    subscribers.set(key, callbacks);
    updatePolling();

    callback(getSnapshot(key));

    return () => {
        const currentCallbacks = subscribers.get(key);
        currentCallbacks?.delete(callback);
        if (!currentCallbacks?.size) {
            subscribers.delete(key);
        }
        updatePolling();
    };
}

export const matrixSessionLockTestUtils = {
    getOwnerId: () => ownerId,
    buildLockKey,
    buildClaimantKey,
    readLockRecord,
    resetForTests: () => {
        ownedLocks.forEach(ownedLock => window.clearInterval(ownedLock.heartbeatId));
        ownedLocks.clear();
        subscribers.clear();
        pendingClaims.clear();
        if (pollIntervalId !== null) {
            window.clearInterval(pollIntervalId);
            pollIntervalId = null;
        }
    },
};
